import { useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react';

export interface TabItem {
	id: string;
	label: string;
	icon?: React.ElementType;
	badge?: number | string | null;
}

interface TabBarProps {
	tabs: TabItem[];
	activeTab: string;
	onTabChange: (id: string) => void;
	hideLabelsOnMobile?: boolean;
}

export function TabBar({ tabs, activeTab, onTabChange, hideLabelsOnMobile }: TabBarProps) {
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const [indicator, setIndicator] = useState({ left: 0, width: 0 });

	const updateIndicator = useCallback(() => {
		const idx = tabs.findIndex((t) => t.id === activeTab);
		const el = tabRefs.current[idx];
		if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
	}, [activeTab, tabs]);

	useLayoutEffect(() => {
		updateIndicator();
	}, [updateIndicator]);

	useEffect(() => {
		window.addEventListener('resize', updateIndicator);
		return () => window.removeEventListener('resize', updateIndicator);
	}, [updateIndicator]);

	return (
		<div className="relative">
			<div className="flex" role="tablist">
				{tabs.map((tab, i) => {
					const Icon = tab.icon;
					const isActive = activeTab === tab.id;
					return (
						<button
							key={tab.id}
							ref={(el) => { tabRefs.current[i] = el; }}
							role="tab"
							aria-selected={isActive}
							onClick={() => onTabChange(tab.id)}
							className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
								isActive ? 'text-primary-300' : 'text-neutral-500 hover:text-neutral-300'
							}`}
						>
							{Icon && <Icon className="size-4" />}
							<span className={hideLabelsOnMobile ? 'hidden sm:inline' : undefined}>
								{tab.label}
							</span>
							{tab.badge != null && (
								<span
									className={`flex size-5 items-center justify-center rounded-full text-[10px] font-semibold ${
										isActive
											? 'bg-primary-500/20 text-primary-300'
											: 'bg-neutral-800 text-neutral-500'
									}`}
								>
									{typeof tab.badge === 'number' && tab.badge > 9 ? '9+' : tab.badge}
								</span>
							)}
						</button>
					);
				})}
			</div>
			<div className="absolute bottom-0 left-0 h-0.5 w-full bg-neutral-800" />
			<div
				className="absolute bottom-0 h-0.5 rounded-full bg-primary-400 transition-all duration-300 ease-out"
				style={{ left: indicator.left, width: indicator.width }}
			/>
		</div>
	);
}
